import { useEffect, useState } from 'react';
import { MessageCircle, Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface TemplateDef {
  key: string;
  title: string;
  description: string;
  category: string;
  defaultValue: string;
}

interface LegacyTemplate {
  key: string;
  value: string;
  updatedAt: number;
}

const CATEGORY_ORDER = ['系統通知', '訂單流程'];
// @module:category-order — 選用組件的分類 TAB 由 compose 注入（例：CATEGORY_ORDER.push('優惠');）
CATEGORY_ORDER.push('夥伴與禮品');
CATEGORY_ORDER.push('優惠');
const LEGACY_TAB_KEY = '系統設定（唯讀）';

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  '系統通知': '觸發式通知訊息，例如訂單確認、付款確認。',
  '訂單流程': '與訂單生命週期相關的互動訊息。',
};
// @module:category-descriptions — 選用組件的分類說明由 compose 注入
CATEGORY_DESCRIPTIONS['夥伴與禮品'] = '推薦夥伴、禮品碼相關訊息。';
CATEGORY_DESCRIPTIONS['優惠'] = '顧客發送「{品牌}優惠 OFFER:xxx」時自動回覆的優惠訊息。';

const TEMPLATES: TemplateDef[] = [
  {
    key: 'whatsapp_order_confirm',
    title: '訂單確認訊息',
    description: '顧客下單後經 WhatsApp 自動發送',
    category: '系統通知',
    defaultValue: `收到你的訂單確認！

*訂單編號：{orderNum}*

請使用 FPS/Payme 付款 *HK$${'{finalPrice}'}* ，並上傳付款憑證。

訂單付款連結：
{orderLink}

如有問題請直接回覆此訊息`,
  },
  {
    key: 'whatsapp_payment_confirmed',
    title: '付款確認通知',
    description: '管理員確認收款後自動發送',
    category: '系統通知',
    defaultValue: `你的訂單 *{orderNum}* 付款已確認，我們會盡快備貨。`,
  },
  {
    key: 'whatsapp_completed',
    title: '訂單完成通知',
    description: '管理員標記訂單完成後自動發送',
    category: '訂單流程',
    defaultValue: `{name}，訂單 *{orderNum}* 已完成送出，多謝支持！`,
  },
  {
    key: 'whatsapp_admin_message_alert',
    title: '客戶訊息轉發（管理員）',
    description: '每條客戶入站 WhatsApp 訊息轉發到管理員號碼的格式（只發管理員，客人不會見到；支援「群組on」切換發去 admin 群組）',
    category: '系統通知',
    defaultValue: `【客戶訊息】{typeLabel}
來電：{from}
時間：{time}
內容：{content}{orderLinkLine}

請到後台「WhatsApp 對話」查看並回覆。`,
  },
  // @module:templates — 選用組件（優惠/推薦/商家等）的模板登記由 compose 注入於此行之下
  {
    key: 'whatsapp_referrer_welcome',
    title: '夥伴歡迎訊息',
    description: '顧客發送 {品牌} R-XXXXXX 時自動回覆',
    category: '夥伴與禮品',
    defaultValue: `歡迎成為 *{brandName}夥伴* ！

👉 專屬連結：{referralLink}
📊 數據看板：{dashboardLink}
👥 社群：{facebookGroup}`,
  },
  {
    key: 'whatsapp_friend_welcome',
    title: '成為夥伴歡迎訊息',
    description: '顧客點擊「成為夥伴」deeplink 發送 WhatsApp 時自動回覆',
    category: '夥伴與禮品',
    defaultValue: `Hi {name}，歡迎加入推薦計劃！

你嘅專屬連結（朋友經 link 落單，你賺 *現金回贈* ）：
👉 {referralLink}

💰 回贈睇呢度：{dashboardLink}
👥 社群：{facebookGroup}`,
  },
  {
    key: 'whatsapp_gift_code',
    title: '禮品碼查詢訊息',
    description: '顧客發送「禮品碼／優惠碼」時自動回覆',
    category: '夥伴與禮品',
    defaultValue: `🎁 你嘅 *專屬連結* ：
👉 {referralLink}

💰 回贈：{dashboardLink}
👥 社群：{facebookGroup}`,
  },
  {
    key: 'whatsapp_commission_paid',
    title: '傭金支付通知',
    description: '後台為夥伴銷賬後自動發送',
    category: '夥伴與禮品',
    defaultValue: `【夥伴通知】💸

你好 {name}，你嘅推薦傭金已經支付！

💰 本次支付：{amount}
📊 累積已收：{totalPaid}
🧾 付款參考：{reference}`,
  },
  {
    key: 'whatsapp_offer_claimed',
    title: '優惠領取成功訊息',
    description: '顧客首次發送「{品牌}優惠 OFFER:xxx」成功領取時發送',
    category: '優惠',
    defaultValue: `🎉 領取成功！【{restaurantName}】{offerTitle}

兌換碼： *{redeemCode}*
📱 到店出示兌換頁：{redeemLink}
{validUntilLine}{redeemInstructions}`,
  },
  {
    key: 'whatsapp_offer_already_claimed',
    title: '優惠重複領取訊息',
    description: '顧客重複領取同一優惠時回覆原有兌換碼',
    category: '優惠',
    defaultValue: `【{restaurantName}】{offerTitle}——你已領取過！

兌換碼： *{redeemCode}*
📱 到店出示兌換頁：{redeemLink}
{validUntilLine}如需重發，請回覆「重發優惠」。`,
  },
  {
    key: 'whatsapp_offer_redeemed',
    title: '優惠兌換成功訊息',
    description: '店員核銷成功後自動發送給顧客',
    category: '優惠',
    defaultValue: `✅ 優惠已成功兌換！

【{restaurantName}】{offerTitle}
兌換碼：{redeemCode}
兌換時間：{redeemedAt}
{reclaimLine}
多謝支持，繼續留意最新優惠！`,
  },
];

const PLACEHOLDER_HINT = '可用變數：{orderNum}、{finalPrice}、{paymentLink}、{orderLink}、{name}、{giftCode}、{giftName}、{referralCode}、{referralLink}、{dashboardLink}、{facebookGroup}、{referralCount}、{totalCommission}';

export default function AdminAhJimContent() {
  const [values, setValues] = useState<Record<string, string>>({});
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [exists, setExists] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>(CATEGORY_ORDER[0]);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [legacyTemplates, setLegacyTemplates] = useState<LegacyTemplate[]>([]);

  useEffect(() => {
    if (legacyTemplates.length === 0 && activeCategory === LEGACY_TAB_KEY) {
      setActiveCategory(CATEGORY_ORDER[0]);
    }
  }, [legacyTemplates, activeCategory]);

  useEffect(() => {
    const defaults: Record<string, string> = {};
    TEMPLATES.forEach((t) => (defaults[t.key] = t.defaultValue));
    setValues(defaults);

    const load = async () => {
      setLoading(true);
      const nextValues: Record<string, string> = { ...defaults };
      const nextExists: Record<string, boolean> = {};
      try {
        await Promise.all(
          TEMPLATES.map(async (t) => {
            const res = await apiFetch(`/api/public/admin/cms/${encodeURIComponent(t.key)}`);
            if (res.ok) {
              const json = await res.json();
              const entry = json.data ?? json;
              nextValues[t.key] = entry.value ?? t.defaultValue;
              nextExists[t.key] = true;
            } else {
              nextExists[t.key] = false;
            }
          })
        );
      } catch (e) {
        setNotice({ type: 'error', text: '載入預設回應失敗' });
      }
      setValues(nextValues);
      setSavedValues(nextValues);
      setExists(nextExists);
      setLoading(false);
    };

    const loadLegacy = async () => {
      try {
        const res = await apiFetch('/api/admin/settings/whatsapp-templates');
        if (res.ok) {
          const json = await res.json();
          setLegacyTemplates(json.data?.items ?? []);
        }
      } catch (e) {
        console.warn('Failed to load legacy WhatsApp templates', e);
      }
    };

    load();
    loadLegacy();
  }, []);

  const showNotice = (type: 'success' | 'error', text: string) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 4000);
  };

  const handleSave = async () => {
    setSaving(true);
    setCleaning(true);
    setNotice(null);
    try {
      await Promise.all(
        TEMPLATES.map(async (t) => {
          const value = values[t.key] ?? t.defaultValue;
          if (exists[t.key]) {
            const res = await apiFetch(`/api/public/admin/cms/${encodeURIComponent(t.key)}`, {
              method: 'PUT',
              body: JSON.stringify({ value, isActive: true }),
            });
            if (!res.ok) throw new Error(`更新 ${t.title} 失敗`);
          } else {
            const res = await apiFetch('/api/public/admin/cms', {
              method: 'POST',
              body: JSON.stringify({
                key: t.key,
                type: 'text',
                value,
                label: t.title,
                description: t.description,
                category: t.category,
                isActive: true,
              }),
            });
            if (!res.ok) throw new Error(`新增 ${t.title} 失敗`);
          }
        })
      );
      setExists(Object.fromEntries(TEMPLATES.map((t) => [t.key, true])));
      setSavedValues({ ...values });

      // Clean up duplicate entries in system_settings after successful CMS save
      try {
        const cleanupRes = await apiFetch('/api/admin/settings/whatsapp-templates', { method: 'DELETE' });
        if (!cleanupRes.ok) {
          console.warn('Failed to clean up system_settings whatsapp templates');
        } else {
          setLegacyTemplates([]);
        }
      } catch (e) {
        console.warn('Cleanup request failed', e);
      }

      showNotice('success', '預設回應已儲存，並已清理重複的系統設置記錄');
    } catch (e: any) {
      showNotice('error', e.message || '儲存失敗');
    } finally {
      setSaving(false);
      setCleaning(false);
    }
  };

  const activeTemplates = TEMPLATES.filter((t) => t.category === activeCategory);
  const isLegacyTab = activeCategory === LEGACY_TAB_KEY;

  return (
    <AdminLayout currentPage="default-responses">
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-red-600" />
            WhatsApp 預設回應
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            以下訊息會在對應觸發點自動發送給顧客。修改後按「儲存」即可生效。
          </p>

          {notice && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
                notice.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800'
                  : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'
              }`}
            >
              {notice.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              {notice.text}
            </div>
          )}

          {loading ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
              <Loader2 size={24} className="animate-spin mx-auto mb-2" />
              加載中...
            </div>
          ) : (
            <>
              <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav className="flex gap-2 flex-wrap">
                  {CATEGORY_ORDER.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        activeCategory === cat
                          ? 'border-red-600 text-red-600 dark:text-red-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                  {legacyTemplates.length > 0 && (
                    <button
                      key={LEGACY_TAB_KEY}
                      onClick={() => setActiveCategory(LEGACY_TAB_KEY)}
                      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        isLegacyTab
                          ? 'border-red-600 text-red-600 dark:text-red-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      {LEGACY_TAB_KEY}
                    </button>
                  )}
                </nav>
              </div>

              <div className="mb-4">
                <h3 className="text-base font-bold text-gray-800 dark:text-white">
                  {isLegacyTab ? LEGACY_TAB_KEY : activeCategory}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isLegacyTab
                    ? '以下項目仍保留在 system_settings，已遷移到 CMS 統一管理。點擊「儲存預設回應」後會自動清理。'
                    : CATEGORY_DESCRIPTIONS[activeCategory]}
                </p>
              </div>

              {isLegacyTab ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {legacyTemplates.map((t) => (
                    <div
                      key={t.key}
                      className="bg-gray-50 dark:bg-gray-800/50 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{t.key}</h3>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">唯讀</span>
                      </div>
                      <textarea
                        value={t.value}
                        readOnly
                        rows={6}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 resize-none font-mono"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {activeTemplates.map((t) => (
                    <div
                      key={t.key}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{t.title}</h3>
                        {(values[t.key] ?? t.defaultValue) !== (savedValues[t.key] ?? t.defaultValue) ? (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">未儲存</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">已儲存</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t.description}</p>
                      <textarea
                        value={values[t.key] ?? t.defaultValue}
                        onChange={(e) => setValues((prev) => ({ ...prev, [t.key]: e.target.value }))}
                        rows={6}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white resize-none font-mono"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">{PLACEHOLDER_HINT}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving || cleaning}
                  className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-60 transition"
                >
                  {saving || cleaning ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  {saving ? '儲存中...' : cleaning ? '清理重複項...' : '儲存預設回應'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
